import type { KitlookData } from 'src/types/kitlooks'
import { useEffect, useRef, useState } from 'react'
import DktLoader from 'src/components/DktLoader/DktLoader'

import { KitlookPurchaseActionsProvider } from '../context/KitlookPurchaseActionsContext'
import { useKitlookActions } from '../hooks/useKitlookActions'
import { KitlookItemSelection } from './KitlookItemSelection'

export interface SelectedProduct {
  productId: string
  skuId: string
  isSelected: boolean
}

interface KitlookItemsConfirmationProps {
  currentKit: KitlookData | null
  closeModal: () => void
}

export function KitlookItemsConfirmation({
  currentKit,
  closeModal,
}: KitlookItemsConfirmationProps) {
  const [localSelectedProducts, setLocalSelectedProducts] = useState<
    SelectedProduct[]
  >([])

  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const purchaseActionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (currentKit) {
      const initial = currentKit.products.map((product) => {
        return {
          productId: product.productId,
          skuId: '',
          isSelected: true,
        }
      })

      setLocalSelectedProducts(initial)
    }
  }, [currentKit])

  const { handlePurchase } = useKitlookActions({
    currentKit,
    selectedProducts: localSelectedProducts,
  })

  const handleToggleProduct = (productId: string) => {
    setLocalSelectedProducts((prev) =>
      prev.map((p) =>
        p.productId === productId ? { ...p, isSelected: !p.isSelected } : p
      )
    )
  }

  const handleSelectSku = (productId: string, skuId: string) => {
    setLocalSelectedProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, skuId } : p))
    )
  }

  const handleAddToCart = async (type: 'buyNow' | 'addToCart') => {
    setIsLoading(true)
    setHasError(false)

    const hasMissingSkuSelected = localSelectedProducts
      .filter((p) => p.isSelected)
      .some((p) => !p.skuId)

    if (hasMissingSkuSelected) {
      setHasError(true)
      setIsLoading(false)

      return
    }

    try {
      await handlePurchase(type)
      closeModal()
    } catch (err) {
      console.error('Erro ao processar compra:', err)
    } finally {
      setIsLoading(false)
    }
  }

  if (!currentKit || localSelectedProducts.length === 0) return null

  return (
    <KitlookPurchaseActionsProvider value={purchaseActionsRef}>
      <div className="flex flex-col gap-6 p-4 bg-white rounded-lg">
        <div className="flex flex-col gap-8 overflow-y-auto max-h-[50vh] md:max-h-none">
          {currentKit.products.map((product) => (
            <KitlookItemSelection
              key={product.productId}
              product={product}
              selectSku={handleSelectSku}
              toggleProduct={handleToggleProduct}
              selectedProducts={localSelectedProducts}
            />
          ))}
        </div>

        <div ref={purchaseActionsRef} className="flex flex-col gap-3 mt-4">
          {hasError && (
            <span className="text-[#AB0009] font-normal text-sm mx-auto text-center">
              Selecione todos os tamanhos para continuar
            </span>
          )}
          <button
            onClick={() => handleAddToCart('buyNow')}
            disabled={isLoading}
            className="w-full h-12 flex items-center justify-center bg-primaryBlue text-white font-semibold text-base rounded-full transition-colors disabled:opacity-70"
          >
            {isLoading ? (
              <DktLoader width={20} variant="white" />
            ) : (
              'Comprar agora'
            )}
          </button>
          <button
            onClick={() => handleAddToCart('addToCart')}
            disabled={isLoading}
            className="w-full h-12 flex items-center justify-center text-primaryBlue font-semibold text-base rounded-full bg-transparent border border-primaryBlue transition-colors disabled:opacity-70"
          >
            {isLoading ? (
              <DktLoader width={20} variant="dkt-blue" />
            ) : (
              'Adicionar ao carrinho'
            )}
          </button>
        </div>
      </div>
    </KitlookPurchaseActionsProvider>
  )
}
